import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  onSnapshot,
  serverTimestamp,
  updateDoc,
  doc,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { motion } from 'framer-motion';

const UserDashboard = () => {
  const { currentUser, userData } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [activeTab, setActiveTab] = useState('bookings');
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    carType: '',
    pickupLocation: '',
    destination: '',
    date: '',
    hours: 1,
    city: '',
    userPhone: userData?.phone || '' 
  });

  useEffect(() => {
    if (!currentUser) {
      setBookings([]);
      setApplications([]);
      setLoading(false);
      return;
    }

    // Fetch user's bookings
    const bookingsQuery = query(
      collection(db, 'bookings'),
      where('userId', '==', currentUser.uid)
    );
    
    const unsubscribeBookings = onSnapshot(bookingsQuery, 
      (snapshot) => {
        const bookingsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setBookings(bookingsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching bookings:', error);
        setLoading(false);
        if (error.code === 'permission-denied') {
          console.log('Permission denied for bookings');
        }
      }
    );

    return () => unsubscribeBookings();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || bookings.length === 0) {
      setApplications([]);
      return;
    }

    // Fetch applications for user's bookings
    const fetchApplications = async () => {
      try {
        const applicationsQuery = query(collection(db, 'applications'));
        const querySnapshot = await getDocs(applicationsQuery);
        const applicationsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter applications for user's bookings
        const userApplications = applicationsData.filter(app => 
          bookings.some(booking => booking.id === app.bookingId)
        );
        setApplications(userApplications);
      } catch (error) {
        console.error('Error fetching applications:', error);
        if (error.code === 'permission-denied') {
          console.log('Permission denied for applications');
        }
      }
    };

    fetchApplications();
  }, [currentUser, bookings]);

  // Update form data when userData changes
  useEffect(() => {
    if (userData?.phone) {
      setFormData(prev => ({
        ...prev,
        userPhone: userData.phone
      }));
    }
  }, [userData]);

  const calculatePrice = (hours) => {
    const baseRate = 200;
    const gst = 0.2;
    const subtotal = baseRate * hours;
    const tax = subtotal * gst;
    const total = subtotal + tax;
    
    return {
      subtotal: subtotal.toFixed(2),
      tax: tax.toFixed(2),
      total: total.toFixed(2)
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      alert('Please log in to create a booking');
      return;
    }

    try {
      const price = calculatePrice(formData.hours);
      
      await addDoc(collection(db, 'bookings'), {
        ...formData,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: `${userData?.firstName} ${userData?.lastName}`,
        userPhone: formData.userPhone,
        status: 'pending',
        price,
        createdAt: serverTimestamp(),
        notifications: {
          user: 'Booking submitted. Waiting for admin approval.',
          admin: `New booking from ${currentUser.email}`,
          drivers: `New booking available in ${formData.city}`
        }
      });

      setShowBookingForm(false);
      setFormData({
        carType: '',
        pickupLocation: '',
        destination: '',
        date: '',
        hours: 1,
        city: '',
        userPhone: userData?.phone || ''
      });
      
      alert('Booking submitted successfully! Waiting for admin approval.');
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Error creating booking. Please try again.');
    }
  };

  const handleAcceptDriver = async (applicationId, driverId, driverName, driverPhone) => {
    try {
      // Update application status
      await updateDoc(doc(db, 'applications', applicationId), {
        status: 'accepted',
        acceptedAt: serverTimestamp()
      });

      // Update booking with driver info
      const application = applications.find(app => app.id === applicationId);
      if (application) {
        await updateDoc(doc(db, 'bookings', application.bookingId), {
          driverId: driverId,
          driverName: driverName,
          driverPhone: driverPhone,
          status: 'driver_assigned',
          'notifications.user': `You have accepted ${driverName}. Contact them at ${driverPhone}`,
          'notifications.driver': `Your application has been accepted! Contact user at ${formData.userPhone}`
        });
      }

      alert(`You have accepted ${driverName}. Contact them at ${driverPhone}`);
    } catch (error) {
      console.error('Error accepting driver:', error);
      alert('Error accepting driver. Please try again.');
    }
  };

  const cities = [
    'Dehradun', 'Roorkee', 'Haridwar', 'Haldwani', 'Kashipur',
    'Pantnagar', 'Nainital', 'Mussoorie', 'Rudrapur', 'Almora',
    'Rishikesh', 'Ranikhet', 'Pithoragarh', 'Chamoli', 'Uttarkashi'
  ];

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700">Please Log In</h2>
          <p className="text-gray-500">You need to be logged in to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome, {userData?.firstName || 'User'}!
          </h1>
          <p className="text-gray-600">Manage your driver bookings and applications</p>
          <p className="text-sm text-blue-600 mt-2">
            Your Phone: {userData?.phone || 'Not provided'}
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-lg mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {[
                { id: 'bookings', name: 'My Bookings', count: bookings.length },
                { id: 'applications', name: 'Driver Applications', count: applications.length }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.name} ({tab.count})
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'bookings' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-semibold">Book a Driver</h2>
                  <button
                    onClick={() => setShowBookingForm(!showBookingForm)}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {showBookingForm ? 'Cancel' : 'New Booking'}
                  </button>
                </div>

                {showBookingForm && (
                  <motion.form
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    onSubmit={handleSubmit}
                    className="space-y-4 bg-gray-50 p-6 rounded-lg"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Car Type</label>
                        <select
                          required
                          value={formData.carType}
                          onChange={(e) => setFormData({...formData, carType: e.target.value})}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="">Select Car Type</option>
                          <option value="wagonR">Maruti Suzuki Wagon R</option>
                          <option value="swift">Maruti Suzuki Swift</option>
                          <option value="Dzire">Maruti Suzuki Dzire</option>
                          <option value="Hondacity">Honda City</option>
                          <option value="scorpio">Mahindra Scorpio</option>
                          <option value="creta">Hyundai Creta</option>
                          <option value="i20">Hyundai i20</option>
                          <option value="i10">Hyundai i10</option>
                          <option value="ertiga">Ertiga</option>
                          <option value="baleno">Maruti Suzuki Baleno</option>
                          <option value="brezza">Maruti Suzuki Brezza</option>
                          <option value="nexon">Tata Nexon</option>
                          <option value="alto">Maruti Suzuki Alto</option>
                          <option value="santro">Hyundai Santro Xing</option>
                          <option value="kwid">Renault Kwid</option>
                          <option value="venue">Hyundai Venue</option>
                          <option value="Other">Other Car</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">City</label>
                        <select
                          required
                          value={formData.city}
                          onChange={(e) => setFormData({...formData, city: e.target.value})}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="">Select City</option>
                          {cities.map(city => (
                            <option key={city} value={city}>{city}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Pickup Location</label>
                        <input
                          type="text"
                          required
                          value={formData.pickupLocation}
                          onChange={(e) => setFormData({...formData, pickupLocation: e.target.value})}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          placeholder="Enter pickup location"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Destination</label>
                        <input
                          type="text"
                          required
                          value={formData.destination}
                          onChange={(e) => setFormData({...formData, destination: e.target.value})}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          placeholder="Enter destination"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Date & Time</label>
                        <input
                          type="datetime-local"
                          required
                          value={formData.date}
                          onChange={(e) => setFormData({...formData, date: e.target.value})}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Hours</label>
                        <input
                          type="number"
                          min="1"
                          required
                          value={formData.hours}
                          onChange={(e) => setFormData({...formData, hours: parseInt(e.target.value)})}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700">Your Phone Number</label>
                        <input
                          type="tel"
                          required
                          value={formData.userPhone}
                          onChange={(e) => setFormData({...formData, userPhone: e.target.value})}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          placeholder="Enter your phone number for drivers to contact you"
                        />
                      </div>
                    </div>

                    {/* Price Calculation */}
                    {formData.hours > 0 && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-blue-50 p-4 rounded-lg"
                      >
                        <h3 className="font-semibold mb-2">Price Calculation</h3>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span>Subtotal (Rs-200/hr × {formData.hours} hrs):</span>
                            <span>Rs {calculatePrice(formData.hours).subtotal}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>GST (20%):</span>
                            <span>Rs {calculatePrice(formData.hours).tax}</span>
                          </div>
                          <div className="flex justify-between font-semibold border-t pt-1">
                            <span>Total:</span>
                            <span>Rs {calculatePrice(formData.hours).total}</span>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    <button
                      type="submit"
                      className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold"
                    >
                      Submit Booking
                    </button>
                  </motion.form>
                )}

                {/* Bookings List */}
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold">Your Bookings</h3>
                  {bookings.map((booking, index) => (
                    <motion.div
                      key={booking.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="border rounded-lg p-4 bg-white shadow-sm"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-semibold text-lg">{booking.carType} - {booking.city}</h4>
                          <p className="text-sm text-gray-600">
                            {booking.pickupLocation} → {booking.destination}
                          </p>
                          <p className="text-sm text-gray-600">
                            {booking.date ? new Date(booking.date).toLocaleString() : 'Date not set'}
                          </p>
                          <p className="text-sm font-semibold">Total: Rs {booking.price?.total}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          booking.status === 'approved' ? 'bg-green-100 text-green-800' :
                          booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          booking.status === 'driver_assigned' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {booking.status?.replace('_', ' ') || 'pending'}
                        </span>
                      </div>
                      
                      {booking.driverName && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg">
                          <p className="text-sm font-semibold text-green-800">
                            ✅ Driver Assigned: {booking.driverName}
                          </p>
                          <p className="text-sm text-green-700">
                            Contact: {booking.driverPhone}
                          </p>
                        </div>
                      )}
                      
                      {booking.notifications?.user && (
                        <p className="text-xs text-blue-600 mt-2 bg-blue-50 p-2 rounded">
                          📢 {booking.notifications.user}
                        </p>
                      )}
                    </motion.div>
                  ))}
                  
                  {bookings.length === 0 && (
                    <p className="text-gray-500 text-center py-8">
                      No bookings yet. Create your first booking above!
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'applications' && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold">Driver Applications</h3>
                
                {applications.map((application, index) => (
                  <motion.div
                    key={application.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border rounded-lg p-6 bg-white shadow-sm"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-lg font-semibold">
                          {application.bookingDetails?.carType} - {application.bookingDetails?.pickupLocation} to {application.bookingDetails?.destination}
                        </h4>
                        <p className="text-gray-600">
                          Applied on {application.appliedAt?.toDate?.() ? new Date(application.appliedAt.toDate()).toLocaleDateString() : 'Recent'}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        application.status === 'accepted' ? 'bg-green-100 text-green-800' :
                        application.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {application.status}
                      </span>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6 mb-4">
                      <div className="space-y-2 text-sm">
                        <p><span className="font-medium">Driver Name:</span> {application.driverName}</p>
                        <p><span className="font-medium">Driver Email:</span> {application.driverEmail}</p>
                        <p><span className="font-medium">Driver Phone:</span> {application.driverPhone}</p>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <p><span className="font-medium">Booking Date:</span> { application.bookingDetails?.date && typeof application.bookingDetails.date.toDate === 'function'  ? application.bookingDetails.date.toDate().toLocaleString() : application.bookingDetails?.date  ? new Date(application.bookingDetails.date).toLocaleString() : 'Not specified' }</p>
                        <p><span className="font-medium">Hours:</span> {application.bookingDetails?.hours}</p>
                        <p><span className="font-medium">Total Pay:</span> Rs {application.bookingDetails?.price?.total}</p>
                      </div>
                    </div>

                    {application.status === 'pending' && (
                      <div className="flex space-x-4">
                        <button
                          onClick={() => handleAcceptDriver(
                            application.id,
                            application.driverId,
                            application.driverName,
                            application.driverPhone
                          )}
                          className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors font-semibold"
                        >
                          Accept Driver
                        </button>
                        <button
                          className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors font-semibold"
                        >
                          View Profile
                        </button>
                      </div>
                    )}

                    {application.status === 'accepted' && (
                      <div className="bg-green-50 p-4 rounded-lg">
                        <p className="text-green-800 font-semibold">
                          ✅ You have accepted this driver. Contact them at: {application.driverPhone}
                        </p>
                      </div>
                    )}
                  </motion.div>
                ))}
                
                {applications.length === 0 && (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🚗</div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                      No driver applications yet
                    </h3>
                    <p className="text-gray-500">
                      Drivers will appear here when they apply for your approved bookings.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;